import { Fragment, useState } from "react";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { canManageScheduleMasterData } from "@/features/auth/permissions";
import {
  groupBadgeCount,
  groupBadgeLabel,
  groupIssuesByCodeSeverity,
  stableIssueKey,
  type IssueGroup,
} from "../lib/group-issues";
import { friendlyIssueLabel } from "../lib/validation-issue-labels";
import { formatEntityLabel } from "../lib/format-entity-label";
import { IssueDetailExpander } from "./issue-detail-expander";
import type { ReassignHandler } from "./reassign-handler";
import type { CourseFocusHandler } from "./course-focus-handler";
import type { IssueSeverity, ScheduleValidationIssue } from "../types";

interface ValidationIssuesCardProps {
  issues: ScheduleValidationIssue[];
  /**
   * Optional handler that, when present, surfaces a "Reassign" link on every
   * issue row that carries both `studentIdentifier` and `courseId`. Hidden
   * for Viewer roles (the permission check happens at the row level so the
   * rest of the card still renders for everyone).
   */
  onReassign?: ReassignHandler;
  /**
   * Optional handler that, when present, turns the course code in each issue's
   * context line into a button that jumps to the calendar and highlights the
   * course. Text is unchanged when omitted (e.g. in isolated tests).
   */
  onCourseFocus?: CourseFocusHandler;
}

/**
 * Severity label for the group header chip. Exhaustive on
 * {@link IssueSeverity} via the `never` default so adding a new severity is
 * a compile-time error rather than a silent fall-through.
 */
function severityLabel(severity: IssueSeverity): string {
  switch (severity) {
    case "Error":
      return "Error";
    case "Warning":
      return "Warning";
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

interface SeverityIconProps {
  severity: IssueSeverity;
}

/**
 * Severity glyph matching the Phase 4 prototype: red triangle for errors,
 * amber circle for warnings. Exhaustive on {@link IssueSeverity} for the
 * same reason as {@link severityLabel}.
 */
function SeverityIcon({ severity }: SeverityIconProps) {
  switch (severity) {
    case "Error":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          data-testid="post-run-severity-icon-error"
          className="text-red-400 shrink-0"
        >
          <path d="M12 3 22 20H2L12 3Z" />
        </svg>
      );
    case "Warning":
      return (
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
          data-testid="post-run-severity-icon-warning"
          className="text-amber-400 shrink-0"
        >
          <circle cx="12" cy="12" r="10" />
        </svg>
      );
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
}

/**
 * True when the issue carries any typed context to render (student, course, or
 * teacher). Stage 1/2 issues with no typed columns fall through to the
 * {@link ScheduleValidationIssue.message} only.
 */
function hasContext(issue: ScheduleValidationIssue): boolean {
  return Boolean(
    issue.studentIdentifier ||
      issue.normalizedStudentIdentifier ||
      issue.courseId ||
      issue.teacherEmail,
  );
}

interface IssueContextLineProps {
  issue: ScheduleValidationIssue;
  onCourseFocus?: CourseFocusHandler;
}

/**
 * The typed-context line for an issue: student · course · teacher. Renders as
 * segments (not a joined string) so the course code can become a button that
 * jumps to the calendar when {@link onCourseFocus} is supplied. Order mirrors
 * the prototype: student → course → teacher.
 */
function IssueContextLine({ issue, onCourseFocus }: IssueContextLineProps) {
  // Each segment carries its own stable key so the separator interleave below
  // doesn't need index keys.
  const segments: { key: string; node: React.ReactNode }[] = [];

  const studentId =
    issue.studentIdentifier ?? issue.normalizedStudentIdentifier;
  if (studentId) {
    segments.push({
      key: "student",
      node: <>Student: {formatEntityLabel(issue.studentName, studentId)}</>,
    });
  }

  if (issue.courseId) {
    const courseId = issue.courseId;
    const label = formatEntityLabel(issue.courseName, courseId);
    const sectionSuffix = issue.sectionLabel
      ? ` — Section ${issue.sectionLabel}`
      : "";
    segments.push({
      key: "course",
      node: (
        <>
          Course:{" "}
          {onCourseFocus ? (
            <button
              type="button"
              onClick={() => onCourseFocus(courseId, issue.sectionLabel)}
              data-testid={`issue-course-focus-${issue.id}`}
              className="text-blue-400 hover:text-blue-300 underline decoration-dotted cursor-pointer"
            >
              {label}
            </button>
          ) : (
            label
          )}
          {sectionSuffix}
        </>
      ),
    });
  }

  if (issue.teacherEmail) {
    segments.push({
      key: "teacher",
      node: <>Teacher: {issue.teacherEmail}</>,
    });
  }

  // Interleave " · " separators between segments.
  return (
    <>
      {segments.map((seg, i) => (
        <Fragment key={seg.key}>
          {i > 0 ? " · " : ""}
          {seg.node}
        </Fragment>
      ))}
    </>
  );
}

interface IssueRowProps {
  issue: ScheduleValidationIssue;
  onReassign?: ReassignHandler;
  onCourseFocus?: CourseFocusHandler;
  canReassign: boolean;
}

/**
 * One issue row: context line, message, an optional "Reassign" action, and —
 * when the issue carries an affected-student roster — an expandable detail
 * region listing who's affected and why. The expand state is per-row so it's
 * independent of the group-level collapse.
 */
function IssueRow({
  issue,
  onReassign,
  onCourseFocus,
  canReassign,
}: IssueRowProps) {
  const [detailOpen, setDetailOpen] = useState(false);
  const showContext = hasContext(issue);
  // Narrow the nullable reassign inputs once into a single object so the
  // onClick closure stays type-safe without re-checking each field at the
  // render site (which is where a double-guard drifts out of sync).
  const reassign =
    canReassign &&
    onReassign != null &&
    issue.studentIdentifier !== null &&
    issue.courseId !== null
      ? {
          handler: onReassign,
          studentId: issue.studentIdentifier,
          courseId: issue.courseId,
        }
      : null;
  // Per-affected-student override: the aggregated NotPlaced issue carries a
  // course but no single student, so the "Reassign" action lives on each
  // affected-student row instead of the issue row. Narrowed once so the closure
  // stays type-safe.
  const perStudentReassign =
    canReassign && onReassign != null && issue.courseId !== null
      ? {
          handler: onReassign,
          courseId: issue.courseId,
          courseName: issue.courseName,
        }
      : null;
  const affected = issue.affectedStudents ?? [];
  const events = issue.events ?? [];
  const hasDetail = affected.length > 0 || events.length > 0;
  const detailRegionId = `issue-detail-${issue.id}`;
  // Toggle copy adapts to whichever evidence the issue carries — an affected-
  // student roster (NotPlaced) or an event breakdown (teacher overload). An
  // issue carries at most one of the two.
  const showLabel =
    affected.length > 0
      ? `Show ${affected.length} affected student${affected.length === 1 ? "" : "s"}`
      : `Show ${events.length} class${events.length === 1 ? "" : "es"}`;
  const hideLabel = affected.length > 0 ? "Hide students" : "Hide classes";

  return (
    <div data-testid={`issue-row-${issue.id}`} className="px-4 py-3 pl-11">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {showContext && (
            <div
              data-testid={`issue-context-${issue.id}`}
              className="text-[12px] text-text-muted mb-0.5"
            >
              <IssueContextLine issue={issue} onCourseFocus={onCourseFocus} />
            </div>
          )}
          <div className="text-[13px] text-text-soft leading-snug">
            {issue.message}
          </div>
          {hasDetail && (
            <button
              type="button"
              aria-expanded={detailOpen}
              aria-controls={detailRegionId}
              onClick={() => setDetailOpen((prev) => !prev)}
              data-testid={`issue-detail-toggle-${issue.id}`}
              className="mt-1 text-[12px] text-blue-400 hover:text-blue-300 cursor-pointer"
            >
              {detailOpen ? hideLabel : showLabel}
            </button>
          )}
        </div>
        {reassign && (
          <button
            type="button"
            onClick={() =>
              reassign.handler(
                reassign.studentId,
                reassign.courseId,
                issue.sectionLabel,
                issue.studentName,
                issue.courseName,
              )
            }
            data-testid={`issue-reassign-${issue.id}`}
            className="text-[12px] text-blue-400 hover:text-blue-300 cursor-pointer shrink-0"
          >
            Reassign
          </button>
        )}
      </div>
      {hasDetail && detailOpen && (
        <IssueDetailExpander
          id={detailRegionId}
          students={affected}
          events={events}
          onCourseFocus={onCourseFocus}
          onReassignStudent={
            perStudentReassign
              ? (studentId, studentName) =>
                  perStudentReassign.handler(
                    studentId,
                    perStudentReassign.courseId,
                    null,
                    studentName,
                    perStudentReassign.courseName,
                  )
              : undefined
          }
        />
      )}
    </div>
  );
}

interface GroupCardProps {
  group: IssueGroup;
  onReassign?: ReassignHandler;
  onCourseFocus?: CourseFocusHandler;
  canReassign: boolean;
}

/**
 * One collapsible group card. Starts collapsed (matches prototype). The
 * outer button is a real `<button>` so keyboard activation and focus
 * styles come "for free" from the design system.
 */
function GroupCard({
  group,
  onReassign,
  onCourseFocus,
  canReassign,
}: GroupCardProps) {
  const [open, setOpen] = useState(false);
  const bodyId = `issue-group-${group.code}-${group.severity}-body`;
  const headerId = `issue-group-${group.code}-${group.severity}-header`;
  const badgeCount = groupBadgeCount(group);
  const badgeLabel = groupBadgeLabel(group);

  return (
    <section
      data-testid={`issue-group-${group.code}-${group.severity}`}
      className="bg-surface border border-border-subtle rounded-lg overflow-hidden"
    >
      <button
        type="button"
        id={headerId}
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((prev) => !prev)}
        data-testid={`issue-group-toggle-${group.code}-${group.severity}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-2/30 cursor-pointer"
      >
        <SeverityIcon severity={group.severity} />
        <span className="text-text-muted text-[12px] uppercase tracking-wider font-semibold">
          {severityLabel(group.severity)}
        </span>
        <span className="flex flex-col min-w-0">
          <span
            data-testid={`issue-group-label-${group.code}-${group.severity}`}
            className="font-semibold text-[14px] text-white truncate"
          >
            {friendlyIssueLabel(group.code)}
          </span>
          <span className="text-[11px] font-mono text-text-faint truncate">
            {group.code}
          </span>
        </span>
        <span
          data-testid={`issue-group-count-${group.code}-${group.severity}`}
          aria-label={badgeLabel}
          className="ml-auto bg-page border border-border-subtle rounded-full px-2.5 py-0.5 text-[12px] font-semibold text-text-soft"
        >
          {badgeCount}
        </span>
      </button>
      {open && (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          data-testid={`issue-group-body-${group.code}-${group.severity}`}
          className="border-t border-border-subtle divide-y divide-border-subtle/50"
        >
          {group.issues.map((issue) => (
            <IssueRow
              key={stableIssueKey(issue)}
              issue={issue}
              onReassign={onReassign}
              onCourseFocus={onCourseFocus}
              canReassign={canReassign}
            />
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Collapsible, grouped presentation of Stage 3 (PostRun) — and any other —
 * validation issues. Groups are keyed by `(code, severity)`; each group
 * starts collapsed so the page loads quietly even on a run with many issues.
 *
 * Pure presentation: callers control the issue list. The Phase 4 tab passes
 * the full {@link useScheduleIssues} result; later phases (e.g. a run
 * comparison page) can filter the list first.
 */
export function ValidationIssuesCard({
  issues,
  onReassign,
  onCourseFocus,
}: ValidationIssuesCardProps) {
  const { user } = useAuth();
  const canReassign = canManageScheduleMasterData(user?.role);
  const groups = groupIssuesByCodeSeverity(issues);
  return (
    <div data-testid="validation-issues-card" className="flex flex-col gap-3">
      {groups.map((group) => (
        <GroupCard
          key={`${group.code}::${group.severity}`}
          group={group}
          onReassign={onReassign}
          onCourseFocus={onCourseFocus}
          canReassign={canReassign}
        />
      ))}
    </div>
  );
}
